import React from 'react';
import logger from '../utils/logger';

// Catch render-phase errors from any descendant and show a fallback instead of
// unmounting the whole app. Chunk-load errors (common after a deploy invalidates
// a lazy bundle) offer a one-click reload.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    logger.error('ErrorBoundary caught', { message: error?.message, stack: info?.componentStack });
  }

  componentDidUpdate(prevProps) {
    // Clear the error when the caller signals a reset (e.g. route change), so a
    // single transient render error on one page no longer pins the WHOLE app on
    // the fallback until a full reload.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  // Full navigation to the landing page: the fallback replaced the entire app
  // (nav included), so an in-app route change isn't possible from here.
  handleGoHome = () => {
    window.location.assign('/home');
  };

  render() {
    if (!this.state.error) return this.props.children;

    const isChunkError =
      this.state.error?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(this.state.error?.message || '');

    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 24,
          background: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>
          {isChunkError ? 'Nouvelle version disponible' : "Une erreur est survenue"}
        </h1>
        <p style={{ maxWidth: 480, margin: 0, opacity: 0.8 }}>
          {isChunkError
            ? "L'application a été mise à jour. Rechargez la page pour continuer."
            : "L'interface a rencontré un problème inattendu. Rechargez la page ou contactez le support si le problème persiste."}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              cursor: 'pointer',
              background: 'var(--primary-color, #1976d2)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
            }}
          >
            Recharger
          </button>
          {!isChunkError && (
            <button
              type="button"
              onClick={this.handleGoHome}
              style={{
                padding: '10px 20px',
                fontSize: 14,
                cursor: 'pointer',
                background: 'transparent',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default, #ccc)',
                borderRadius: 6,
              }}
            >
              Retour à l'accueil
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
